import { useState } from "react";
import { Link } from "wouter";
import { useListProducts, useListSales, useListStaff } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X, Rocket, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "rp_onboarding_dismissed";

interface Step {
  id: string;
  label: string;
  description: string;
  href: string;
  done: boolean;
  optional?: boolean;
}

export function OnboardingChecklist() {
  const { user, store } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  const { data: products } = useListProducts();
  const { data: sales } = useListSales(undefined);
  const { data: staffList } = useListStaff();

  const hasProducts = (products?.length ?? 0) > 0;
  const hasSales = sales?.some(sale => sale.status === "completed") ?? false;
  const hasStaff = (staffList?.length ?? 0) > 0;
  const hasPlan = store?.subscriptionStatus === "active";

  const steps: Step[] = [
    { id: "product", label: "Add your first product", description: "Add a product or service to your inventory", href: "/inventory?add=1", done: hasProducts },
    { id: "sale", label: "Record your first sale", description: "Process a sale to start tracking revenue", href: "/sales/new", done: hasSales },
    { id: "staff", label: "Invite a team member (optional)", description: "You can add staff whenever you're ready", href: "/staff", done: hasStaff, optional: true },
    { id: "plan", label: "Choose a paid plan (optional)", description: "Your trial lets you get started before choosing a plan", href: "/subscribe", done: hasPlan, optional: true },
  ];

  const primarySteps = steps.filter(step => !step.optional);
  const completedCount = primarySteps.filter(step => step.done).length;
  const firstValueComplete = completedCount === primarySteps.length;

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
  };

  const progressPct = Math.round((completedCount / primarySteps.length) * 100);

  return (
    <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-5 mb-6 relative">
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Rocket className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 pr-8">
          <p className="font-semibold">Welcome to RetailPulse{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! 👋</p>
          <p className="text-sm text-muted-foreground mt-0.5">Add a product, then record your first sale. Staff and a paid plan can wait.</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
          <span>{completedCount} of {primarySteps.length} first-value steps complete</span>
          <span>{progressPct}%</span>
        </div>
        <div className="h-1.5 bg-primary/15 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={step.id}>
          {step.optional && index === 2 && (
            <p className="pt-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Optional during your trial</p>
          )}
          <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
            step.done ? "opacity-60" : "bg-background/60 hover:bg-background/80"
          }`}>
            {step.done
              ? <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              : <Circle className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${step.done ? "line-through text-muted-foreground" : ""}`}>{step.label}</p>
              {!step.done && <p className="text-xs text-muted-foreground">{step.description}</p>}
            </div>
            {!step.done && (step.id !== "sale" || hasProducts) && (
              <Link href={step.href}>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs gap-1 flex-shrink-0"
                  onClick={() => trackEvent("onboarding_cta_clicked", { step: step.id })}
                >
                  Go <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            )}
          </div>
          </div>
        ))}
      </div>

      {firstValueComplete && (
        <div className="mt-4 text-center">
          <p className="text-sm font-medium text-primary">🎉 First value reached — your store is ready to keep selling!</p>
        </div>
      )}
    </div>
  );
}
